import { useCallback } from "react";
import { BannerPage } from "../../uiLibrary/bannerPage/BannerPage";
import { Spinner } from "../../uiLibrary/spinner/Spinner";
import { Warning } from "../../uiLibrary/icons/Warning";
import { useSingleton } from "@/other/injection";
import { MessageBusService } from "@/other/messageBus/MessageBusService";
import { useSubscription } from "@/uiLibrary/virtualScroll/useSubscription";
import { usePagination } from "@/uiLibrary/virtualScroll/usePagination";
import { InternalMediaListItem } from "./InternalMediaListItem";
import { internalMediaPrimaryButtonClass } from "./internalMediaActionButton";
import {
    getInternalMediaItemId,
    internalMediaSortCompare,
    subscribeToInternalMedia
} from "./internalMediaQuery";

interface PaginationControls {
    requestAnotherPage?: () => void;
    isRequestingAnotherPage?: boolean;
}

export function InternalMediaListPage() {
    const messageBusService = useSingleton(MessageBusService);

    const createSubscription = useCallback(
        (callback: Parameters<typeof subscribeToInternalMedia>[1]) =>
            subscribeToInternalMedia(messageBusService, callback),
        [messageBusService]
    );

    const { hasResult, items, pagination, resultSetId } = useSubscription({
        createSubscription,
        getItemId: getInternalMediaItemId,
        sortCompare: internalMediaSortCompare
    });

    const paginationControls = usePagination(
        pagination,
        resultSetId
    ) as PaginationControls;

    if (!hasResult) {
        return (
            <BannerPage title="Community" slim>
                <div className="w-full py-12 text-center">
                    <Spinner />
                </div>
            </BannerPage>
        );
    }

    if (items.length === 0) {
        return (
            <BannerPage title="Community" slim>
                <div className="w-full py-12 text-center">
                    <Warning size={50} />
                    <div className="mt-4">No posts available</div>
                </div>
            </BannerPage>
        );
    }

    return (
        <BannerPage title="Community" slim>
            <div className="flex w-full flex-col gap-4">
                {items.map(item => (
                    <InternalMediaListItem
                        key={getInternalMediaItemId(item)}
                        item={item}
                    />
                ))}
                {!pagination.isLastPage &&
                    paginationControls.requestAnotherPage && (
                        <div className="w-full pt-2 text-center">
                            <button
                                type="button"
                                onClick={paginationControls.requestAnotherPage}
                                disabled={
                                    paginationControls.isRequestingAnotherPage
                                }
                                className={internalMediaPrimaryButtonClass}
                            >
                                {paginationControls.isRequestingAnotherPage
                                    ? "Loading..."
                                    : "Load more"}
                            </button>
                        </div>
                    )}
            </div>
        </BannerPage>
    );
}
